import React from 'react';
import { inject, observer } from 'mobx-react';
import { reaction, IReactionDisposer } from 'mobx';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import TiptapEditor from './components/TiptapEditor';
import { setPostImage } from '@images/imageUrls';
import EmojiPicker from '@components/EmojiPicker/h5';
import Toast from '@components/Toast';
import { extractMentionsFromHtml } from '../../../../utils/setPostMentions';
import FeedbackFormFields from '../../components/FeedbackFormFields/h5';
import FeedbackAttachmentDisplay from '../../components/FeedbackAttachmentUpload/h5';
import FeedbackAttachmentProvider from '../../components/FeedbackAttachmentUpload/FeedbackAttachmentProvider';
import FeedbackBottomToolbar from '../../components/FeedbackAttachmentUpload/h5/FeedbackBottomToolbar';
import { isSameEditorContent } from '../../../../utils/tiptapEditorContent';
import TabNav  from '@components/tabNav';
import { BASE_IMG_URL } from '@images/imageUrls';
import { readactivityList } from '@server';
import { releaseModalBodyScrollLock, syncModalBodyScrollLock } from '@utils/modalScrollLock';
import PublishSuccessPopup from './components/PublishSuccessPopup';

const toastSuccess = (msg: string) => {
    try { (Toast as any)?.success ? Toast.success(msg) : alert(msg); } catch { alert(msg); }
};
const toastError = (msg: string) => {
    try { (Toast as any)?.error ? Toast.error(msg) : alert(msg); } catch { alert(msg); }
};

interface SetFeedbackPostH5PageProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteAction;
    user?: UserAction;
    thread?: ThreadAction;
    setPost?: any;
    setPostData: (data: any) => void;
    onPublishPost: (options?: { onSuccess?: (payload: { threadId?: string | number; result: any; isEdit: boolean }) => void }) => void;
    loading?: boolean;
    editorReady?: boolean;
}

interface SetFeedbackPostH5State {
    loading: boolean;
    error: any;
    pageLoaded: any;
    title: string;
    hasContent: boolean;
    showEmojiPicker: boolean;
    hasDraft: boolean;
    restoreDraftLoading: boolean;
    saveDraftLoading: boolean;
    editorInstanceKey: number;
    navList: any;
    navCurrentIndex: any;
    hasStartedEditing: boolean;
    publishSuccessVisible: boolean;
    successThreadId?: string | number | null;
    successActivityList: any[];
    successActivityLoading: boolean;
}

@inject('app', 'site', 'user', 'thread', 'setPost')
@observer
class SetFeedbackPostH5 extends BasePage<SetFeedbackPostH5PageProps, SetFeedbackPostH5State> {
    titleDisposer: IReactionDisposer | null = null;
    problemSolvedDisposer: IReactionDisposer | null = null;
    editorRef = React.createRef<any>();
    emojiPickerSelection: { from: number; to: number } | null = null;

    state: SetFeedbackPostH5State = {
        loading: false,
        error: null,
        pageLoaded: false,
        title: '',
        hasContent: false,
        showEmojiPicker: false,
        hasDraft: false,
        restoreDraftLoading: false,
        saveDraftLoading: false,
        editorInstanceKey: 0,
        navList: [
        { name: '图文', id: 0, type: '' },
        { name: '问题反馈', id: 0, type: '1' },
        ],    // tab切换
        navCurrentIndex: 1, // 当前tab的索引
        hasStartedEditing: false,
        publishSuccessVisible: false,
        successThreadId: null,
        successActivityList: [],
        successActivityLoading: false,
    };

    async componentDidMount() {
        this.onPageMount();
        const store = this.props.setPost;
        if (store?.postData && store.postData.type !== 2) {
            this.props.setPostData({ type: 2 });
        }

        const initTitle = store?.postData?.title || '';
        const initContent = store?.postData?.content || '';
        if (initTitle) {
            this.setState({ title: initTitle });
        }
        this.updateHasContent(initTitle, initContent);
        if (initTitle.trim() || this.hasValidEditorContent(initContent)) {
            this.setState({ hasStartedEditing: true });
        }

        this.titleDisposer = reaction(
            () => store?.postData?.title,
            (newTitle) => {
                const nextTitle = newTitle || '';
                this.setState({ title: nextTitle });
                this.updateHasContent(nextTitle, store?.postData?.content || '');
            }
        );
        this.problemSolvedDisposer = reaction(
            () => ({
                problemId: String(store?.postData?.problemId || ''),
                solvedCount: Number(store?.postData?.problemSolvedCount ?? 0) || 0,
                countLoaded: !!store?.postData?.problemSolvedCountLoaded,
            }),
            ({ problemId, solvedCount, countLoaded }) => {
                if (problemId && countLoaded && solvedCount <= 0) {
                    toastError('暂未找到相关已解决问题帖');
                }
            }
        );
        this.checkDraft();
    }

    componentWillUnmount() {
        if (this.titleDisposer) this.titleDisposer();
        if (this.problemSolvedDisposer) this.problemSolvedDisposer();
        releaseModalBodyScrollLock(this.state.publishSuccessVisible);
    }

    componentDidUpdate(_: SetFeedbackPostH5PageProps, prevState: SetFeedbackPostH5State) {
        syncModalBodyScrollLock(this.state.publishSuccessVisible, prevState.publishSuccessVisible);
    }

    handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value;
        this.setState((prevState) => ({
            title,
            hasStartedEditing: prevState.hasStartedEditing || title.trim() !== '',
        }));
        this.updateHasContent(title, this.props.setPost?.postData?.content || '');
        this.props.setPostData({ title });
    }

    handleContentChange = (content: string) => {
        const prevContent = this.props.setPost?.postData?.content || '';
        if (isSameEditorContent(prevContent, content)) {
            return;
        }
        const parsedMentions = extractMentionsFromHtml(content);
        if (!this.state.hasStartedEditing && this.hasValidEditorContent(content)) {
            this.setState({ hasStartedEditing: true });
        }
        this.updateHasContent(this.state.title, content);
        this.props.setPostData({ content, mentions: parsedMentions });
    }

    hasValidEditorContent = (content: string) => {
        if (!content) return false;

        const cleanContent = content.replace(/<[^>]*>/g, '').trim();
        if (cleanContent) return true;

        if (/<img[^>]*>/i.test(content)) return true;
        if (/<video[^>]*>/i.test(content)) return true;
        if (/<audio[^>]*>/i.test(content)) return true;

        return false;
    }

    updateHasContent = (title: string, content: string) => {
        const hasContent = title.trim() !== '' || this.hasValidEditorContent(content || '');
        if (hasContent !== this.state.hasContent) {
            this.setState({ hasContent });
        }
    }

    checkDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.checkDraft) return;

        const ret = await setPost.checkDraft(2);
        if (ret.code === 0 && ret.data?.hasDraft) {
            this.setState({ hasDraft: true });
        }
    }

    handleRestoreDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.restoreDraft || !setPost?.draftData?.draftThreadId) {
            toastError('无法恢复草稿');
            return;
        }

        this.setState({ restoreDraftLoading: true });
        try {
            const ret = await setPost.restoreDraft(setPost.draftData.draftThreadId);
            if (ret.code === 0) {
                toastSuccess('草稿已恢复');
                this.setState((prev) => ({
                    hasDraft: false,
                    editorInstanceKey: prev.editorInstanceKey + 1,
                }));
            } else {
                toastError(ret.message || '恢复草稿失败');
            }
        } catch (error) {
            toastError('恢复草稿出错');
        } finally {
            this.setState({ restoreDraftLoading: false });
        }
    }

    handleSaveDraftManually = async () => {
        const { setPost } = this.props;
        if (!setPost?.saveDraftManually) {
            toastError('缺少保存方法');
            return;
        }

        if (!this.hasValidEditorContent(this.props.setPost?.postData?.content || '')) {
            toastError('请输入内容');
            return;
        }

        this.setState({ saveDraftLoading: true });
        try {
            const ret = await setPost.saveDraftManually(null, null, 2);
            if (ret.code === 0) {
                toastSuccess('草稿已保存');
            } else {
                toastError(ret.message || '保存草稿失败');
            }
        } catch (error) {
            toastError('保存草稿出错');
        } finally {
            this.setState({ saveDraftLoading: false });
        }
    }

    fetchSuccessActivities = async () => {
        this.setState({ successActivityLoading: true });
        try {
            const res: any = await readactivityList({
                'page[number]': 1,
                'page[size]': 3,
            });
            const activityData = res?.data && res.data.data ? res.data.data : res?.data;
            if (res?.code === 0) {
                this.setState({ successActivityList: Array.isArray(activityData) ? activityData : [] });
            } else {
                this.setState({ successActivityList: [] });
            }
        } catch (_) {
            this.setState({ successActivityList: [] });
        } finally {
            this.setState({ successActivityLoading: false });
        }
    }

    handlePublish = () => {
        try {
            this.props.setPost?.addLog?.('ui_publish_click', {
                titleLength: (this.state.title || '').length,
                contentLength: (this.props.setPost?.postData?.content || '').length,
                hasContent: !!this.state.hasContent,
            });
        } catch (_) {}

        this.props.onPublishPost({
            onSuccess: ({ threadId, isEdit }) => {
                if (isEdit || !threadId) {
                    toastSuccess(isEdit ? '编辑成功' : '发布成功');
                    if (threadId) {
                        this.props.history.push('/threadDetail/' + threadId);
                    }
                    return;
                }

                toastSuccess('发布成功');
                this.setState({
                    publishSuccessVisible: true,
                    successThreadId: threadId,
                    successActivityList: [],
                });
                this.fetchSuccessActivities();
            },
        });
    }

    handleSuccessBackHome = () => {
        this.setState({ publishSuccessVisible: false }, () => {
            this.props.history.push('/');
        });
    }

    handleSuccessViewDetail = () => {
        const threadId = this.state.successThreadId;
        this.setState({ publishSuccessVisible: false }, () => {
            if (threadId) {
                this.props.history.push('/threadDetail/' + threadId);
            }
        });
    }

    handleEmojiClick = () => {
        const editor = this.editorRef.current;
        if (editor?.state?.selection) {
            const { from, to } = editor.state.selection;
            this.emojiPickerSelection = { from, to };
        }
        editor?.commands?.blur?.();
        this.setState({ showEmojiPicker: true });
    }

    handleEmojiSelect = (emoji: string, emojiCode?: string, meta?: { category?: string }) => {
        if (emojiCode === ':delete:') {
            this.handleEmojiDelete();
            return;
        }

        const editor = this.editorRef.current;
        if (editor) {
            const selection = this.emojiPickerSelection || editor.state?.selection || null;
            const isImageEmoji = typeof emoji === 'string' && emoji.startsWith('http');
            const isDefaultCategory = meta?.category === '默认';
            const defaultEmojiSize = 28;
            if (isImageEmoji) {
                const size = isDefaultCategory ? defaultEmojiSize : 59;
                const attrs = isDefaultCategory
                    ? ({ alt: emojiCode, src: emoji, 'data-emoji': 'default', width: size, height: size, style: 'display:inline-block;vertical-align:middle;' } as any)
                    : ({ alt: emojiCode, src: emoji, 'data-emoji': 'large', width: size, height: size, style: 'display:inline-block;vertical-align:middle;' } as any);

                const content = [
                    { type: 'image', attrs },
                    { type: 'text', text: ' ' },
                ];
                const ok = selection
                    ? editor.chain().insertContentAt(selection, content).run()
                    : editor.chain().insertContent(content).run();
                if (!ok) {
                    const fallbackHtml = `<img src="${emoji}" alt="emoji" data-emoji="${isDefaultCategory ? 'default' : 'large'}" style="width: ${size}px; height: ${size}px; display: inline-block; vertical-align: middle;" /> `;
                    (selection
                        ? editor.chain().insertContentAt(selection, fallbackHtml)
                        : editor.chain().insertContent(fallbackHtml))
                        .run();
                }
            } else {
                const content = `${emoji} `;
                (selection
                    ? editor.chain().insertContentAt(selection, content)
                    : editor.chain().insertContent(content))
                    .run();
            }
            if (editor.state?.selection) {
                const { from, to } = editor.state.selection;
                this.emojiPickerSelection = { from, to };
            }
            return;
        }

        let emojiHtml = '';
        if (emoji && emoji.startsWith && emoji.startsWith('http')) {
            const isDefault = meta?.category === '默认';
            const size = isDefault ? '28px' : '59px';
            emojiHtml = `<img src="${emoji}" class="emoji-img" style="width: ${size} !important; height: ${size} !important; vertical-align: middle;" alt="${emojiCode || 'emoji'}" />`;
        } else {
            emojiHtml = `<span class="emoji-text">${emoji}</span>`;
        }
        const newContent = (this.props.setPost?.postData?.content || '') + emojiHtml + '&nbsp;';
        this.props.setPostData({ content: newContent });
        this.updateHasContent(this.state.title, newContent);
    }

    handleEmojiClose = () => {
        this.emojiPickerSelection = null;
        this.setState({ showEmojiPicker: false });
    }

    handleEmojiDelete = () => {
        const editor = this.editorRef.current;
        if (!editor) return;
        const { state, view } = editor;
        const selection = this.emojiPickerSelection || state.selection;
        const { from, to, empty } = selection as any;
        const $from = state.doc.resolve(from);
        if (!empty) {
            view.dispatch(state.tr.delete(from, to));
            this.emojiPickerSelection = { from, to: from };
            return;
        }
        if (from > 0) {
            const textBeforeCursor = $from.parent?.textContent?.slice(0, $from.parentOffset) || '';
            const lastChar = (Array.from(textBeforeCursor).pop() as string | undefined) || '';
            const deleteLength = lastChar.length || 1;
            const deleteFrom = Math.max(from - deleteLength, 0);
            view.dispatch(state.tr.delete(deleteFrom, from));
            this.emojiPickerSelection = { from: deleteFrom, to: deleteFrom };
        }
    }

    // tab切换
    navClick = (item, index) => {
        this.setState({
            navCurrentIndex: index
        }, () => {
            if(index == 0) {
                this.props.history.push('/setPost')
            }
        })
    }

    handleRelatedFeedbackClick = () => {
        const problemId = String(this.props.setPost?.postData?.problemId || '');
        const productId = String(this.props.setPost?.postData?.productId || '');
        if (!problemId) return;

        const params = new URLSearchParams({ problemId });
        if (productId) params.set('productId', productId);
        window.open(`/feedbackList?${params.toString()}`, '_blank');
    }

    render() {
        const { setPost } = this.props;
        const { navList, navCurrentIndex, hasStartedEditing } = this.state;
        const displayTitle = setPost?.postData?.title ?? this.state.title ?? '';
        const initialEditorContent = setPost?.postData?.content ?? '';
        const problemId = String(setPost?.postData?.problemId || '');
        const solvedCount = Number(setPost?.postData?.problemSolvedCount ?? 0) || 0;
        const canSaveDraft = this.hasValidEditorContent(initialEditorContent || '');
        const canPublish = !!(
            this.hasValidEditorContent(initialEditorContent || '') &&
            problemId &&
            setPost?.postData?.productId &&
            (setPost?.postData?.systemVersion || '').trim()
        );
        const { editorReady = false } = this.props;
        const isRestoredDraft = !!this.props.setPost?.draftData?.isRestoreDraft || !!this.props.setPost?.postData?.isDraft;
        const isEditingPublishedPost = !!this.props.setPost?.postData?.id && !isRestoredDraft;

        return (
            <div className={styles.wrapper}>
                {!hasStartedEditing && (
                    <div className={styles.navListBox}>
                        <TabNav navList={navList} currentIndex={navCurrentIndex} navClick={(item:any, index:any) => this.navClick(item, index)}></TabNav>
                    </div>
                )}
                {problemId && solvedCount > 0 && (
                    <div className={styles.relatedEnter} onClick={this.handleRelatedFeedbackClick}>
                        <img src={`${BASE_IMG_URL}/feedback/icon9.png`} alt="" className={styles.icon1} />
                        <div className={styles.text}>为你找到已解决相关问题{solvedCount}篇，去查看</div>
                        <img src={`${BASE_IMG_URL}/feedback/icon10.png`} alt="" className={styles.icon2} />
                    </div>
                )}
                {Number(this.props.setPost?.postData?.type) === 0 && (
                    <div className={styles.titleBox}>
                        <input
                            className={styles.titleInput}
                            placeholder="添加标题(30字以内)"
                            type="text"
                            maxLength={30}
                            value={displayTitle}
                            onChange={this.handleTitleChange}
                        />
                    </div>
                )}

                <FeedbackAttachmentProvider>
                    <div className={styles.editorBox}>
                        {editorReady ? (
                            <TiptapEditor
                                key={`${setPost?.postData?.id || 'new'}-${this.state.editorInstanceKey}`}
                                initialContent={initialEditorContent}
                                onContentChange={this.handleContentChange}
                                editorRef={this.editorRef}
                                type={2}
                                emojiList={this.props.site?.emojiList}
                            />
                        ) : null}
                    </div>

                    <FeedbackAttachmentDisplay />
                </FeedbackAttachmentProvider>

                <FeedbackBottomToolbar onEmojiClick={this.handleEmojiClick} />

                <FeedbackFormFields
                    autoOpenProblemPicker={
                        editorReady &&
                        !isEditingPublishedPost &&
                        !isRestoredDraft &&
                        !problemId
                    }
                />

                <EmojiPicker
                    visible={this.state.showEmojiPicker}
                    onClose={this.handleEmojiClose}
                    onSelect={this.handleEmojiSelect}
                    onDelete={this.handleEmojiDelete}
                    showEmojiTab
                />

                <div className={styles.fillBetween} />

                <div className={styles.footerBox}>
                    {!isEditingPublishedPost && (
                        <button
                            className={styles.draftSaveBtn}
                            onClick={this.handleSaveDraftManually}
                            disabled={!canSaveDraft || this.state.saveDraftLoading}
                        >
                            <img
                                src={this.state.saveDraftLoading ? setPostImage.draft : setPostImage.draftActive}
                                alt=""
                                className={styles.draftIcon}
                            />
                            <span>{this.state.saveDraftLoading ? '保存中...' : '存草稿'}</span>
                        </button>
                    )}
                    {!isEditingPublishedPost && (
                        <button
                            className={styles.restoreDraftBtn}
                            onClick={this.handleRestoreDraft}
                            disabled={!this.state.hasDraft || this.state.restoreDraftLoading}
                        >
                            <span>{this.state.restoreDraftLoading ? '恢复中...' : '恢复草稿'}</span>
                        </button>
                    )}
                    <button
                        className={`${styles.publishBtn} ${isEditingPublishedPost ? styles.publishBtnFull : ''}`}
                        onClick={this.handlePublish}
                        disabled={!canPublish || this.props.loading}
                    >
                        <span className={styles.publishBtnText}>{this.props.loading ? '发布中...' : '发布'}</span>
                    </button>
                </div>

                <PublishSuccessPopup
                    visible={this.state.publishSuccessVisible}
                    avatar={this.props.user?.avatar}
                    threadId={this.state.successThreadId}
                    activityList={this.state.successActivityList}
                    activityLoading={this.state.successActivityLoading}
                    onClose={() => this.setState({ publishSuccessVisible: false })}
                    onBackHome={this.handleSuccessBackHome}
                    onViewDetail={this.handleSuccessViewDetail}
                />
            </div>
        );
    }
}

export default withRouter(SetFeedbackPostH5);
